import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Grid,
  Alert,
  useTheme,
  Tooltip,
  Button
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import TocIcon from '@mui/icons-material/Toc';
import InfoIcon from '@mui/icons-material/Info';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HelpIcon from '@mui/icons-material/Help';
import VerifiedIcon from '@mui/icons-material/Verified';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import SegmentTree from './SegmentTree';
import DataPanel from './DataPanel';
import RawEdiViewer from './RawEdiViewer';
import CodeHighlight from './CodeHighlight';
import EdifactReference from './EdifactReference';
import ValidationResults from './ValidationResults';
import validateEancom from '../utils/eancomValidator';
import eancomParser, { buildParsedFromSegments } from '../utils/eancomParser';
import ExternalValidation from './ExternalValidation';
import standEancomConfig from '../utils/standEancomConfig';
import { getQualifierCodeExplanation } from '../utils/eancomSegmentDescriptions';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`edi-tabpanel-${index}`}
      aria-labelledby={`edi-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const EdiViewer = ({ ediContent, parsedData, isLoading, openReferenceTab = false, onTabOpened = () => {} }) => {
  const [tabValue, setTabValue] = useState(2); // Start with Raw EDI tab (index 2)
  const [validationResults, setValidationResults] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationTabIndex, setValidationTabIndex] = useState(0);
  const [detectedDistribution, setDetectedDistribution] = useState(null);
  const [autoValidate, setAutoValidate] = useState(() => {
    try { return JSON.parse(localStorage.getItem('autoValidate') || 'true'); } catch { return true; }
  });
  const [focusSegmentIndex, setFocusSegmentIndex] = useState(null);
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [segmentQuery, setSegmentQuery] = useState('');
  const [segmentTypeFilter, setSegmentTypeFilter] = useState('');
  const [activeMessageJump, setActiveMessageJump] = useState('');
  const theme = useTheme();

  // Effect to handle opening the reference tab when requested
  useEffect(() => {
    if (openReferenceTab) {
      setTabValue(3); // Index of the reference tab
      onTabOpened(); // Notify parent that tab was opened
    }
  }, [openReferenceTab, onTabOpened]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    try { localStorage.setItem('edi_tab', String(newValue)); } catch {}
  };

  const handleValidationTabChange = (event, newValue) => {
    setValidationTabIndex(newValue);
  };

  // Modify the runValidation function to use STAND config
  const runValidation = () => {
    if (!parsedData) return;
    
    setIsValidating(true);
    
    // Use setTimeout to avoid blocking the UI
    setTimeout(() => {
      try {
        // First detect distribution type based on message content
        const distributionType = standEancomConfig.detectDistributionType(parsedData);
        setDetectedDistribution(distributionType);
        
        // Run validation
        const results = validateEancom(parsedData);
        setValidationResults(results);
      } catch (error) {
        console.error('Error during validation:', error);
        setValidationResults({
          valid: false,
          errors: [{
            type: 'system',
            message: 'An error occurred during validation: ' + error.message,
            location: 'system'
          }],
          warnings: []
        });
      } finally {
        setIsValidating(false);
      }
    }, 100);
  };

  // Auto run validation on new parsed data (if validation results not already provided)
  useEffect(() => {
    if (parsedData) {
      if (parsedData.validationResults) {
        // Use pre-computed validation results
        setValidationResults(parsedData.validationResults);
      } else if (autoValidate) {
        // Run validation if not already done
      runValidation();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedData, autoValidate]);

  // Restore last selected tab
  useEffect(() => {
    try {
      const saved = localStorage.getItem('edi_tab');
      if (saved !== null) setTabValue(parseInt(saved, 10));
    } catch {}
  }, []);

  // Persist autoValidate preference
  useEffect(() => {
    try { localStorage.setItem('autoValidate', JSON.stringify(autoValidate)); } catch {}
  }, [autoValidate]);

  // Navigate to segment index from validation
  const handleNavigateToLocation = (location) => {
    if (!location) return;
    // Attempt to extract numeric index from patterns like segment:XYZ:12 or segment:XYZ[12]
    const match = location.match(/segment:[A-Z]+(?::|\[)(\d+)/);
    if (match && match[1]) {
      const idx = parseInt(match[1], 10);
      setTabValue(0); // Structured view
      setTimeout(() => {
        setFocusSegmentIndex(idx);
        const el = document.getElementById(`segment-${idx}`);
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
    }
  };

  const downloadValidationJson = () => {
    if (!validationResults) return;
    const blob = new Blob([JSON.stringify(validationResults, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `validation-results-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Detect multiple messages (UNH..UNT slices) and compute per-message meta
  const messageSlices = React.useMemo(() => {
    const segs = parsedData?.segments || [];
    const slices = [];
    let start = -1;
    for (let i = 0; i < segs.length; i++) {
      const s = segs[i];
      if (s.id === 'UNH') start = i;
      if (s.id === 'UNT' && start >= 0) {
        const unh = segs[start];
        const ref = unh?.elements?.[0] || String(slices.length + 1);
        const type = Array.isArray(unh?.elements?.[1]) ? unh.elements[1][0] : (parsedData?.messageType || '');
        slices.push({ index: slices.length + 1, start, end: i, ref, type });
        start = -1;
      }
    }
    // Attach error/warning counts if available
    if (validationResults) {
      const errByMsg = new Map();
      const warnByMsg = new Map();
      (validationResults.errors || []).forEach(e => {
        const mi = e.messageIndex || 0;
        errByMsg.set(mi, (errByMsg.get(mi) || 0) + 1);
      });
      (validationResults.warnings || []).forEach(w => {
        const mi = w.messageIndex || 0;
        warnByMsg.set(mi, (warnByMsg.get(mi) || 0) + 1);
      });
      slices.forEach((sl, i) => {
        const key = i + 1;
        sl.errors = errByMsg.get(key) || 0;
        sl.warnings = warnByMsg.get(key) || 0;
      });
    }
    // Build per-message parsedData objects
    const perDocs = slices.map(sl => ({
      meta: sl,
      parsed: buildParsedFromSegments(segs.slice(sl.start, sl.end + 1), { interchangeHeader: parsedData?.interchangeHeader, delimiters: parsedData?.delimiters })
    }));
    return perDocs;
  }, [parsedData, validationResults]);

  const copyRawEdi = () => {
    if (!ediContent) return;
    navigator.clipboard?.writeText(ediContent).catch(() => {});
  };

  const downloadRawEdi = () => {
    if (!ediContent) return;
    const blob = new Blob([ediContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `document-${Date.now()}.edi`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onSearchSegment = () => {
    if (!parsedData?.segments || !segmentQuery) return;
    const idx = parsedData.segments.findIndex(s => (s.id || '').toLowerCase().includes(segmentQuery.toLowerCase()));
    if (idx >= 0) {
      setTabValue(0);
      setTimeout(() => handleNavigateToLocation(`segment:${parsedData.segments[idx].id}:${idx}`), 10);
    }
  };

  const flaggedMap = React.useMemo(() => {
    const map = new Map();
    const ensure = (idx) => {
      if (!map.has(idx)) {
        map.set(idx, { errors: 0, warnings: 0, infos: 0, codes: new Set(), messages: { errors: [], warnings: [], infos: [] } });
      }
      return map.get(idx);
    };
    const pushIssue = (idx, code, type, msg) => {
      const cur = ensure(idx);
      cur[type] += 1;
      if (code) cur.codes.add(code);
      cur.messages[type].push(msg);
    };
    const extract = (loc) => {
      const m = loc && loc.match(/segment:([A-Z]+)?(?::|\[)(\d+)/);
      return m && m[2] ? { code: m[1] || null, rawIdx: parseInt(m[2], 10) } : null;
    };
    // Build a mapping from segment code to list of global indices
    const codeToIndices = new Map();
    (parsedData?.segments || []).forEach((seg, gi) => {
      if (!codeToIndices.has(seg.id)) codeToIndices.set(seg.id, []);
      codeToIndices.get(seg.id).push(gi);
    });
    const maybeMapToGlobal = (code, rawIdx) => {
      if (code && codeToIndices.has(code)) {
        const arr = codeToIndices.get(code);
        // If rawIdx is within arr, treat as local (by-code) index
        if (arr[rawIdx] !== undefined) return arr[rawIdx];
      }
      // Fallback: treat as global index if valid
      if (parsedData?.segments && parsedData.segments[rawIdx]) return rawIdx;
      return null;
    };
    (validationResults?.errors || []).forEach(e => {
      const res = extract(e.location);
      if (res) {
        const g = maybeMapToGlobal(res.code, res.rawIdx);
        if (g !== null) pushIssue(g, res.code, 'errors', e.message || 'Error');
      }
    });
    (validationResults?.warnings || []).forEach(w => {
      const res = extract(w.location);
      if (res) {
        const g = maybeMapToGlobal(res.code, res.rawIdx);
        if (g !== null) pushIssue(g, res.code, 'warnings', w.message || 'Warning');
      }
    });
    (validationResults?.info || []).forEach(i => {
      const res = extract(i.location);
      if (res) {
        const g = maybeMapToGlobal(res.code, res.rawIdx);
        if (g !== null) pushIssue(g, res.code, 'infos', i.message || 'Info');
      }
    });
    return map;
  }, [validationResults, parsedData]);

  const isFlaggedForSegment = (segmentId, idx) => {
    const flags = flaggedMap.get(idx);
    if (!flags) return false;
    // If we captured codes, require a matching code; if older issues had no codes, be conservative and assume match only if no codes exist
    if (flags.codes && flags.codes.size > 0) return flags.codes.has(segmentId);
    return false;
  };

  const unmappedIssues = React.useMemo(() => {
    const issues = [];
    const parse = (loc) => {
      const m = loc && loc.match(/segment:([A-Z]+)?(?::|\[)(\d+)/);
      return m && m[2] ? { code: m[1] || null, rawIdx: parseInt(m[2], 10) } : null;
    };
    const codeToIndices = new Map();
    (parsedData?.segments || []).forEach((seg, gi) => {
      if (!codeToIndices.has(seg.id)) codeToIndices.set(seg.id, []);
      codeToIndices.get(seg.id).push(gi);
    });
    const isMappable = (code, rawIdx) => {
      if (code && codeToIndices.has(code)) {
        const arr = codeToIndices.get(code);
        if (arr[rawIdx] !== undefined) return true;
      }
      return !!(parsedData?.segments && parsedData.segments[rawIdx]);
    };
    const add = (type, it, parsed) => {
      const detail = parsed && parsed.code ? `${parsed.code} #${parsed.rawIdx + 1}` : null;
      issues.push({ type, message: it.message || 'Issue', location: it.location || '', detail, code: parsed?.code || null, ordinal: parsed ? parsed.rawIdx + 1 : null });
    };
    (validationResults?.errors || []).forEach(it => { const p = parse(it.location); if (!p || !isMappable(p.code, p.rawIdx)) add('error', it, p); });
    (validationResults?.warnings || []).forEach(it => { const p = parse(it.location); if (!p || !isMappable(p.code, p.rawIdx)) add('warning', it, p); });
    (validationResults?.info || []).forEach(it => { const p = parse(it.location); if (!p || !isMappable(p.code, p.rawIdx)) add('info', it, p); });
    return issues;
  }, [validationResults, parsedData]);

  const inferHighlightIndex = (segmentId, flags) => {
    // Heuristics: highlight first element which usually holds qualifier/number
    if (['LIN', 'NAD', 'DTM', 'QTY', 'MOA', 'PRI', 'RFF', 'TAX', 'UNS', 'CUX'].includes(segmentId)) return 0;
    return -1;
  };

  const parseLocationDetails = (loc) => {
    const m = loc && loc.match(/segment:([A-Z]+)?(?::|\[)(\d+)/);
    return m && m[2] ? { code: m[1] || null, ordinal: parseInt(m[2], 10) + 1 } : null;
  };

  const segmentTypeCounts = React.useMemo(() => {
    const map = new Map();
    (parsedData?.segments || []).forEach(seg => {
      map.set(seg.id, (map.get(seg.id) || 0) + 1);
    });
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [parsedData]);

  // Get date qualifier description
  const getDateQualifierDescription = (qualifier) => {
    const descriptions = {
      '137': 'Document date',
      '2': 'Requested delivery date',
      '64': 'Earliest delivery date',
      '63': 'Latest delivery date',
      '10': 'Requested shipment date',
      '11': 'Dispatch date',
      '35': 'Actual delivery date',
      '36': 'Expiry date',
      '7': 'Effective date/time',
      '13': 'Terms discount due date/time',
      '69': 'Delivery date/time, promised for',
      '171': 'Reference date',
      '200': 'Pick-up/collection date/time',
      '325': 'Tax point date',
      '76': 'Production/manufacture date'
    };
    
    return descriptions[qualifier] || `Date type ${qualifier}`;
  };

  if (isLoading) {
    return (
      <Box 
        sx={{ 
          p: 4, 
          mt: 4, 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200
        }}
      >
        <CircularProgress size={60} color="primary" />
        <Typography variant="h6" sx={{ mt: 3 }}>
          Processing EDI content...
        </Typography>
      </Box>
    );
  }

  if (!parsedData) {
    return null;
  }

  return (
    <Box>
      <Box 
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          backgroundColor: theme.palette.background.paper
        }}
      >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
            aria-label="EDI data views"
          >
          <Tab 
            icon={<TocIcon />} 
            label="Structured View" 
            id="edi-tab-0" 
            aria-controls="edi-tabpanel-0" 
          />
          <Tab 
            icon={<InfoIcon />} 
            label="Summary" 
            id="edi-tab-1" 
            aria-controls="edi-tabpanel-1" 
          />
          <Tab 
            icon={<CodeIcon />} 
            label="Raw EDI" 
            id="edi-tab-2" 
            aria-controls="edi-tabpanel-2" 
          />
          <Tab 
            icon={<HelpIcon />} 
            label="Reference" 
            id="edi-tab-3" 
            aria-controls="edi-tabpanel-3" 
          />
          <Tab 
            icon={<VerifiedIcon />} 
            label="Validation" 
            id="edi-tab-4" 
            aria-controls="edi-tabpanel-4"
          />
        </Tabs>
      </Box>

      {/* Structured View Tab */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              Message Structure
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              {messageSlices.length > 1 && (
                <select
                  value={activeMessageJump}
                  onChange={e => {
                    const v = e.target.value;
                    setActiveMessageJump(v);
                    if (v) {
                      const idx = parseInt(v, 10);
                      const sl = messageSlices.find(m => m.meta.index === idx)?.meta;
                      if (sl) {
                        setTabValue(0);
                        setTimeout(() => handleNavigateToLocation(`segment:UNH:${sl.start}`), 10);
                      }
                    }
                  }}
                  style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${theme.palette.divider}`, background: 'transparent', color: 'inherit' }}
                  title="Jump to message"
                >
                  <option value="">All messages ({messageSlices.length})</option>
                  {messageSlices.map(({ meta: m }) => (
                    <option key={m.index} value={m.index}>
                      Msg #{m.index}: {m.type || 'UNK'} {m.ref ? `(${m.ref})` : ''}{typeof m.errors === 'number' ? ` • ${m.errors}E/${m.warnings || 0}W` : ''}
                    </option>
                  ))}
                </select>
              )}
              <Button size="small" variant={issuesOnly ? 'contained' : 'outlined'} color="warning" onClick={() => setIssuesOnly(v => !v)}>
                {issuesOnly ? 'Showing flagged' : 'Show only flagged'}
              </Button>
              <select
                value={segmentTypeFilter}
                onChange={e => setSegmentTypeFilter(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${theme.palette.divider}`, background: 'transparent', color: 'inherit' }}
              >
                <option value="">All segments</option>
                {segmentTypeCounts.map(([id, count]) => (
                  <option key={id} value={id}>{id} ({count})</option>
                ))}
              </select>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <input
                  placeholder="Find segment (e.g., NAD)"
                  value={segmentQuery}
                  onChange={e => setSegmentQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') onSearchSegment(); }}
                  style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${theme.palette.divider}`, background: 'transparent', color: 'inherit' }}
                />
                <Button size="small" variant="outlined" onClick={onSearchSegment}>Search</Button>
              </Box>
              {validationResults && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 1 }}>
                  <Chip
                    color={(validationResults.errors?.length || 0) === 0 ? 'success' : 'error'}
                    variant="filled"
                    size="small"
                    label={(validationResults.errors?.length || 0) === 0 ? 'Valid' : `${validationResults.errors.length} error(s)`}
                  />
                  {(validationResults.warnings?.length || 0) > 0 && (
                    <Chip
                      color="warning"
                      variant="outlined"
                      size="small"
                      label={`${validationResults.warnings.length} warning(s)`}
                    />
                  )}
                </Box>
              )}
            </Box>
          </Box>

          {/* Validation status banner */}
          {validationResults && (
            <Alert
              severity={(validationResults.errors?.length || 0) === 0 ? 'success' : 'error'}
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => setTabValue(4)}>
                  View validation
                </Button>
              }
            >
              {(validationResults.errors?.length || 0) === 0
                ? `Valid document${(validationResults.warnings?.length || 0) > 0 ? ` • ${validationResults.warnings.length} warning(s)` : ''}`
                : `Errors found: ${validationResults.errors.length}${(validationResults.warnings?.length || 0) > 0 ? ` • ${validationResults.warnings.length} warning(s)` : ''}`}
            </Alert>
          )}

          {parsedData.interchangeHeader && (
            <DataPanel
              title="Interchange Header"
              data={[
                { 
                  label: "Sender", 
                  value: parsedData.interchangeHeader.sender || 'N/A' 
                },
                { 
                  label: "Recipient", 
                  value: parsedData.interchangeHeader.recipient || 'N/A' 
                },
                { 
                  label: "Date/Time", 
                  value: parsedData.interchangeHeader.date || 'N/A'
                },
                { 
                  label: "Control Reference", 
                  value: parsedData.interchangeHeader.controlRef || 'N/A',
                  code: parsedData.interchangeHeader.controlRef,
                  type: "qualifier"
                }
              ]}
              tag="UNB"
              elevation={0}
              sx={{ mb: 3 }}
            />
          )}

          {/* Display segments by groups using the new SegmentTree component */}

          {parsedData.segments && parsedData.segments
            .filter((segment, index) => !issuesOnly || isFlaggedForSegment(segment.id, index))
            .filter(segment => !segmentTypeFilter || segment.id === segmentTypeFilter)
            .map((segment, index) => {
              const idx = parsedData.segments.indexOf(segment);
              const flags = flaggedMap.get(idx) || { errors: 0, warnings: 0, infos: 0, codes: new Set(), messages: { errors: [], warnings: [], infos: [] } };
              const isFlagged = isFlaggedForSegment(segment.id, idx);
              let bg = 'transparent';
              if (flags.errors > 0) bg = 'rgba(244, 67, 54, 0.08)';
              else if (flags.warnings > 0) bg = 'rgba(255, 152, 0, 0.08)';
              else if (flags.infos > 0) bg = 'rgba(33, 150, 243, 0.06)';
              return (
                <Box key={`${segment.id}-${idx}`} id={`segment-${idx}`} sx={{
                  borderRadius: 1,
                  outline: focusSegmentIndex === idx ? `2px solid ${theme.palette.info.main}` : 'none',
                  outlineOffset: focusSegmentIndex === idx ? '2px' : 0,
                  transition: 'outline-color 0.2s ease',
                  backgroundColor: isFlagged ? bg : 'transparent'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <SegmentTree segment={segment} delimiters={parsedData.delimiters} highlightFirstElement={isFlagged && inferHighlightIndex(segment.id, flags) === 0} />
                    {isFlagged && (
                      <Tooltip
                        title={
                          <Box sx={{ p: 0.5 }}>
                            {flags.messages.errors.map((m, i) => (
                              <Typography key={`e-${i}`} variant="caption" color="error">• {m}</Typography>
                            ))}
                            {flags.messages.warnings.map((m, i) => (
                              <Typography key={`w-${i}`} variant="caption" color="warning.main" display="block">• {m}</Typography>
                            ))}
                            {flags.messages.infos.map((m, i) => (
                              <Typography key={`i-${i}`} variant="caption" color="info.main" display="block">• {m}</Typography>
                            ))}
                          </Box>
                        }
                        arrow
                      >
                        <Box sx={{ display: 'flex', gap: 0.5, pr: 1 }}>
                          {flags.errors > 0 && (
                            <Chip size="small" color="error" label={flags.errors} />
                          )}
                          {flags.warnings > 0 && (
                            <Chip size="small" color="warning" label={flags.warnings} />
                          )}
                          {flags.infos > 0 && (
                            <Chip size="small" color="info" label={flags.infos} />
                          )}
                        </Box>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}

          {/* When showing only flagged, if no visible segments match but there are issues without precise locations, explain why */}
          {issuesOnly && (!parsedData.segments || parsedData.segments.every((seg, i) => !isFlaggedForSegment(seg.id, i))) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {unmappedIssues.length > 0 ? (
                <>
                  Some issues refer to missing or non-addressable segments, so there is nothing to highlight in the structure view.
                  <Box sx={{ mt: 1 }}>
                    {unmappedIssues.slice(0, 6).map((u, idx) => (
                      <Typography key={idx} variant="body2" sx={{ display: 'block' }}>
                        - {u.message}
                        {u.code && (<>{' '}<strong>Expected/affected:</strong> {u.code}{u.ordinal ? ` #${u.ordinal}` : ''}</>)}
                        {u.location && (<>{' '}<em>(Location: {u.location})</em></>)}
                      </Typography>
                    ))}
                    {unmappedIssues.length > 6 && (
                      <Typography variant="caption" color="text.secondary">(+{unmappedIssues.length - 6} more)</Typography>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  No major errors. This document is valid. Displaying is limited because the filter is set to flagged-only.
                  {(validationResults?.warnings?.length || 0) > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {(validationResults.warnings || []).slice(0, 5).map((w, i) => {
                        const d = parseLocationDetails(w.location);
                        return (
                          <Typography key={i} variant="body2" sx={{ display: 'block' }}>
                            - {w.message}
                            {d?.code && (<>{' '}<strong>Segment:</strong> {d.code}{d?.ordinal ? ` #${d.ordinal}` : ''}</>)}
                          </Typography>
                        );
                      })}
                      {(validationResults.warnings || []).length > 5 && (
                        <Typography variant="caption" color="text.secondary">(+{validationResults.warnings.length - 5} more warnings)</Typography>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Alert>
          )}
        </Box>
      </TabPanel>

      {/* Summary Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ px: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            Message Summary
          </Typography>

          {/* Interchange messages overview */}
          {messageSlices.length > 1 && (
            <Grid container spacing={3} sx={{ mb: 1 }}>
              <Grid item xs={12}>
                <DataPanel title="Interchange Messages" subtitle="Multiple messages detected in this file" elevation={1}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                    <Chip color="primary" label={`Total messages: ${messageSlices.length}`} />
                    <Chip color="secondary" variant="outlined" label={`${(validationResults?.errors || []).length} total errors`} />
                    {(validationResults?.warnings || []).length > 0 && (
                      <Chip color="warning" variant="outlined" label={`${validationResults.warnings.length} total warnings`} />
                    )}
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Reference</TableCell>
                          <TableCell>Line Items</TableCell>
                          <TableCell>Total Amount</TableCell>
                          <TableCell align="right">Errors</TableCell>
                          <TableCell align="right">Warnings</TableCell>
                          <TableCell align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {messageSlices.map(({ meta: m, parsed }) => (
                          <TableRow key={m.index} hover>
                            <TableCell>#{m.index}</TableCell>
                            <TableCell>
                              <Chip 
                                label={parsed.messageType || m.type || 'Unknown'} 
                                size="small" 
                                variant="outlined" 
                                color="primary"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {parsed.messageReference || m.ref || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {parsed.lineItems?.length || 0} items
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {parsed.documentTotals?.documentTotal 
                                  ? `${parsed.documentTotals.currency || ''} ${parsed.documentTotals.documentTotal.toFixed(2)}`
                                  : '-'
                                }
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip size="small" color={(m.errors || 0) > 0 ? 'error' : 'success'} label={m.errors ?? 0} />
                            </TableCell>
                            <TableCell align="right">
                              <Chip size="small" color={(m.warnings || 0) > 0 ? 'warning' : 'default'} label={m.warnings ?? 0} />
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setActiveMessageJump(String(m.index));
                                  setTabValue(0);
                                  setTimeout(() => handleNavigateToLocation(`segment:UNH:${m.start}`), 10);
                                }}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </DataPanel>
              </Grid>
            </Grid>
          )}

          <Grid container spacing={3}>
            {/* Top row: Message Information and Document Totals summary (per-document when multiple) */}
            <Grid item xs={12} md={6}>
              <DataPanel
                title={messageSlices.length > 1 ? `Message Information (${messageSlices.length} Messages)` : 'Message Information'}
                data={messageSlices.length > 1 ? [
                  { 
                    label: "Message Types", 
                    value: (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {[...new Set(messageSlices.map(({ parsed }) => parsed.messageType || 'Unknown'))].map((type, index) => (
                          <Chip 
                            key={index}
                            label={type} 
                            size="small" 
                            variant="outlined" 
                            color="primary"
                          />
                        ))}
                      </Box>
                    )
                  },
                  { 
                    label: "Version", 
                    value: parsedData.version || 'Unknown' 
                  },
                  { 
                    label: "Message References", 
                    value: (
                      <Box sx={{ mt: 1 }}>
                        {messageSlices.map(({ meta: m, parsed }, index) => (
                          <Box key={index} sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            mb: 0.5,
                            p: 1,
                            backgroundColor: theme => theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.05)' 
                              : 'rgba(0, 0, 0, 0.03)',
                            borderRadius: 1
                          }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, mr: 1, minWidth: 50 }}>
                              Msg #{m.index}:
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', mr: 2 }}>
                              {parsed.messageReference || m.ref || 'N/A'}
                            </Typography>
                            <Chip 
                              label={parsed.messageType || m.type || 'Unknown'} 
                              size="small" 
                              variant="outlined"
                            />
                          </Box>
                        ))}
                      </Box>
                    )
                  }
                ] : [
                  { 
                    label: "Message Type", 
                    value: parsedData.messageType || 'Unknown',
                    code: parsedData.messageType,
                    type: "segment"
                  },
                  { 
                    label: "Version", 
                    value: parsedData.version || 'Unknown' 
                  },
                  { 
                    label: "Message Reference", 
                    value: parsedData.messageReference || 'N/A',
                    code: parsedData.messageReference,
                    type: "element"
                  }
                ]}
                elevation={1}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <DataPanel
                title={messageSlices.length > 1 ? 'Document Totals (All Messages)' : 'Document Totals'}
                tag={<AssessmentIcon fontSize="small" />}
                elevation={1}
              >
                {/* Summary info */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={4}>
                    <Box sx={{ p: 1, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="h6" color="primary" sx={{ fontWeight: 500 }}>
                         {messageSlices.length > 1 ? messageSlices.reduce((s, { parsed }) => s + (parsed.documentTotals?.lineCount || 0), 0) : (parsedData.documentTotals?.lineCount || '0')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Line Items
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Box sx={{ p: 1, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="h6" color="primary" sx={{ fontWeight: 500 }}>
                         {messageSlices.length > 1 ? messageSlices.reduce((s, { parsed }) => s + (parsed.documentTotals?.packageCount || 0), 0) : (parsedData.documentTotals?.packageCount || '0')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Packages
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Box sx={{ p: 1, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1, textAlign: 'center' }}>
                      <Typography variant="h6" color="primary" sx={{ fontWeight: 500 }}>
                         {messageSlices.length > 1 ? messageSlices.reduce((s, { parsed }) => s + ((parsed.documentTotals?.segmentCount) || Object.values(parsed.segmentCounts || {}).reduce((sum, count) => sum + count, 0)), 0) : (parsedData.documentTotals?.segmentCount || 
                          Object.values(parsedData.segmentCounts || {}).reduce((sum, count) => sum + count, 0))}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Segments
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
                
                {/* Currency info */}
                {parsedData.documentTotals?.currency && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Currency</Typography>
                    <Chip 
                      label={parsedData.documentTotals.currency} 
                      color="primary" 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>
                )}
                
                {/* Simple monetary amounts */}
                {(messageSlices.length > 1 ? messageSlices.some(({ parsed }) => parsed.documentTotals?.documentTotal != null || parsed.documentTotals?.lineItemTotal != null || parsed.documentTotals?.taxTotal != null) : (parsedData.documentTotals?.documentTotal != null || 
                  parsedData.documentTotals?.lineItemTotal != null || 
                  parsedData.documentTotals?.taxTotal != null)) && (
                  <TableContainer sx={{ mb: 0 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Amount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                           {messageSlices.length > 1 ? (
                             messageSlices.map(({ parsed }, i) => parsed.documentTotals?.taxableAmount != null && (
                               <TableRow key={`taxable-${i}`}>
                                 <TableCell>Taxable Amount (Msg #{i+1})</TableCell>
                                 <TableCell align="right">
                                   <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                     {(parsed.documentTotals.currency || '')} {parsed.documentTotals.taxableAmount.toFixed(2)}
                                   </Typography>
                                 </TableCell>
                               </TableRow>
                             ))
                           ) : (
                             parsedData.documentTotals?.taxableAmount != null && (
                            <TableRow>
                              <TableCell>Taxable Amount</TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {parsedData.documentTotals.currency || ''} {parsedData.documentTotals.taxableAmount.toFixed(2)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                           ))}
                           {messageSlices.length > 1 ? (
                             messageSlices.map(({ parsed }, i) => parsed.documentTotals?.lineItemTotal != null && (
                               <TableRow key={`lines-${i}`}>
                                 <TableCell>Line Items Total (Msg #{i+1})</TableCell>
                                 <TableCell align="right">
                                   <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                     {(parsed.documentTotals.currency || '')} {parsed.documentTotals.lineItemTotal.toFixed(2)}
                                   </Typography>
                                 </TableCell>
                               </TableRow>
                             ))
                           ) : (
                             parsedData.documentTotals?.lineItemTotal != null && (
                            <TableRow>
                              <TableCell>Line Items Total</TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {parsedData.documentTotals.currency || ''} {parsedData.documentTotals.lineItemTotal.toFixed(2)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                           ))}
                           {messageSlices.length > 1 ? (
                             messageSlices.map(({ parsed }, i) => parsed.documentTotals?.taxTotal != null && (
                               <TableRow key={`tax-${i}`}>
                                 <TableCell>Tax Total (Msg #{i+1})</TableCell>
                                 <TableCell align="right">
                                   <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                     {(parsed.documentTotals.currency || '')} {parsed.documentTotals.taxTotal.toFixed(2)}
                                   </Typography>
                                 </TableCell>
                               </TableRow>
                             ))
                           ) : (
                             parsedData.documentTotals?.taxTotal != null && (
                            <TableRow>
                              <TableCell>Tax Total</TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {parsedData.documentTotals.currency || ''} {parsedData.documentTotals.taxTotal.toFixed(2)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                           ))}
                           {messageSlices.length > 1 ? (
                             messageSlices.map(({ parsed }, i) => parsed.documentTotals?.vatAmount != null && parsed.documentTotals?.taxTotal == null && (
                               <TableRow key={`vat-${i}`}>
                                 <TableCell>VAT Amount (Msg #{i+1})</TableCell>
                                 <TableCell align="right">
                                   <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                     {(parsed.documentTotals.currency || '')} {parsed.documentTotals.vatAmount.toFixed(2)}
                                   </Typography>
                                 </TableCell>
                               </TableRow>
                             ))
                           ) : (
                             parsedData.documentTotals?.vatAmount != null && parsedData.documentTotals?.taxTotal == null && (
                            <TableRow>
                              <TableCell>VAT Amount</TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {parsedData.documentTotals.currency || ''} {parsedData.documentTotals.vatAmount.toFixed(2)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                           ))}
                           {messageSlices.length > 1 ? (
                             messageSlices.map(({ parsed }, i) => parsed.documentTotals?.grossTotal != null && (
                               <TableRow key={`gross-${i}`}>
                                 <TableCell>Gross Total (incl. VAT) (Msg #{i+1})</TableCell>
                                 <TableCell align="right">
                                   <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                     {(parsed.documentTotals.currency || '')} {parsed.documentTotals.grossTotal.toFixed(2)}
                                   </Typography>
                                 </TableCell>
                               </TableRow>
                             ))
                           ) : (
                             parsedData.documentTotals?.grossTotal != null && (
                            <TableRow>
                              <TableCell>Gross Total (incl. VAT)</TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {parsedData.documentTotals.currency || ''} {parsedData.documentTotals.grossTotal.toFixed(2)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                           ))}
                           {messageSlices.length > 1 ? (
                             messageSlices.map(({ parsed }, i) => parsed.documentTotals?.documentTotal != null && (
                               <TableRow key={`doc-${i}`} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                                 <TableCell>
                                   <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                     Document Total (Msg #{i+1})
                                   </Typography>
                                 </TableCell>
                                 <TableCell align="right">
                                   <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                     {(parsed.documentTotals.currency || '')} {parsed.documentTotals.documentTotal.toFixed(2)}
                                   </Typography>
                                 </TableCell>
                               </TableRow>
                             ))
                           ) : (
                             parsedData.documentTotals?.documentTotal != null && (
                            <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Document Total
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {parsedData.documentTotals.currency || ''} {parsedData.documentTotals.documentTotal.toFixed(2)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                           ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                )}

                {/* VAT breakdown table if present */}
                {(parsedData.documentTotals?.taxBreakdown || []).length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>VAT Breakdown</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell align="right">Rate %</TableCell>
                            <TableCell align="right">Taxable</TableCell>
                            <TableCell align="right">VAT</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {parsedData.documentTotals.taxBreakdown.map((tb, i) => (
                            <TableRow key={i}>
                              <TableCell>{tb.type || 'VAT'}</TableCell>
                              <TableCell>{tb.category || '-'}</TableCell>
                              <TableCell align="right">{tb.rate != null ? tb.rate.toString() : '-'}</TableCell>
                              <TableCell align="right">
                                {tb.taxableAmount != null ? (
                                  <>{parsedData.documentTotals.currency || ''} {tb.taxableAmount.toFixed(2)}</>
                                ) : '-'}
                              </TableCell>
                              <TableCell align="right">
                                {tb.taxAmount != null ? (
                                  <>{parsedData.documentTotals.currency || ''} {tb.taxAmount.toFixed(2)}</>
                                ) : '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </DataPanel>
            </Grid>

            {/* Second row: Trading Partners and Key Dates */}
            <Grid item xs={12} md={7}>
              <DataPanel
                title={messageSlices.length > 1 ? "Trading Partners (All Messages)" : "Trading Partners"}
                subtitle="NAD segment qualifiers"
                data={[
                  { 
                    label: "Primary Parties",
                    value: (
                      <Grid container spacing={2}>
                        {/* Sender */}
                        <Grid item xs={12} md={6}>
                          <Box sx={{ 
                            p: 1.5, 
                            bgcolor: theme => theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.05)' 
                              : 'rgba(0, 0, 0, 0.02)', 
                            borderRadius: 1 
                          }}>
                            <Typography variant="subtitle2" component="div" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Chip 
                                label={parsedData.sender?.type || 'UNK'} 
                                size="small" 
                                color="primary" 
                                sx={{ mr: 1, minWidth: '32px' }} 
                              />
                              Sender ({parsedData.sender?.typeDescription || 'Unknown'})
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {parsedData.sender?.name || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: <CodeHighlight 
                                code={parsedData.sender?.id || 'N/A'} 
                                type="qualifier" 
                                tooltip="NAD segment party identifier"
                              />
                            </Typography>
                          </Box>
                        </Grid>
                        
                        {/* Recipient */}
                        <Grid item xs={12} md={6}>
                          <Box sx={{ 
                            p: 1.5, 
                            bgcolor: theme => theme.palette.mode === 'dark' 
                              ? 'rgba(255, 255, 255, 0.05)' 
                              : 'rgba(0, 0, 0, 0.02)', 
                            borderRadius: 1 
                          }}>
                            <Typography variant="subtitle2" component="div" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              <Chip 
                                label={parsedData.recipient?.type || 'UNK'} 
                                size="small" 
                                color="primary" 
                                sx={{ mr: 1, minWidth: '32px' }} 
                              />
                              Recipient ({parsedData.recipient?.typeDescription || 'Unknown'})
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              {parsedData.recipient?.name || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: <CodeHighlight 
                                code={parsedData.recipient?.id || 'N/A'} 
                                type="qualifier" 
                                tooltip="NAD segment party identifier"
                              />
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    )
                  },
                  
                  ...((() => {
                    // Aggregate unique parties from all messages
                    const allParties = {};
                    
                    if (messageSlices.length > 1) {
                      messageSlices.forEach(({ parsed }) => {
                        if (parsed.parties) {
                          Object.entries(parsed.parties).forEach(([code, party]) => {
                            if (!allParties[code] || !allParties[code].name) {
                              allParties[code] = party;
                            }
                          });
                        }
                      });
                    } else {
                      Object.assign(allParties, parsedData.parties || {});
                    }
                    
                    return Object.keys(allParties).length > 0 ? [
                      {
                        label: messageSlices.length > 1 ? "Additional Trading Partners (Across All Messages)" : "Additional Trading Partners",
                      value: (
                        <Grid container spacing={2}>
                            {Object.entries(allParties).map(([code, party]) => (
                            <Grid item xs={12} md={6} lg={4} key={code}>
                              <Box sx={{ 
                                p: 1.5, 
                                  bgcolor: theme => theme.palette.mode === 'dark' 
                                    ? 'rgba(255, 255, 255, 0.05)' 
                                    : 'rgba(0, 0, 0, 0.02)', 
                                borderRadius: 1,
                                height: '100%'
                              }}>
                                <Typography variant="subtitle2" component="div" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                  <Chip 
                                    label={code} 
                                    size="small" 
                                    color="primary" 
                                    sx={{ mr: 1, minWidth: '32px' }} 
                                  />
                                  {party.typeDescription}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  {party.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ID: {party.id}
                                </Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      )
                    }
                    ] : [];
                  })()),
                  
                  {
                    label: "NAD Segment Roles",
                    value: (
                      <Box sx={{ p: 1.5, bgcolor: 'rgba(0, 0, 0, 0.03)', borderRadius: 1 }}>
                        <Grid container spacing={1}>
                          <Grid item xs={6} sm={4} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Chip label="BY" size="small" variant="outlined" sx={{ mr: 1, minWidth: '32px' }} />
                              <Typography variant="caption">Buyer</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={4} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Chip label="SU" size="small" variant="outlined" sx={{ mr: 1, minWidth: '32px' }} />
                              <Typography variant="caption">Supplier</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={4} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Chip label="SE" size="small" variant="outlined" sx={{ mr: 1, minWidth: '32px' }} />
                              <Typography variant="caption">Seller</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={4} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Chip label="IV" size="small" variant="outlined" sx={{ mr: 1, minWidth: '32px' }} />
                              <Typography variant="caption">Invoicee</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={4} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Chip label="DP" size="small" variant="outlined" sx={{ mr: 1, minWidth: '32px' }} />
                              <Typography variant="caption">Delivery party</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={4} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Chip label="ST" size="small" variant="outlined" sx={{ mr: 1, minWidth: '32px' }} />
                              <Typography variant="caption">Ship to</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={4} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Chip label="CN" size="small" variant="outlined" sx={{ mr: 1, minWidth: '32px' }} />
                              <Typography variant="caption">Consignee</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={4} md={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Chip label="II" size="small" variant="outlined" sx={{ mr: 1, minWidth: '32px' }} />
                              <Typography variant="caption">Invoice issuer</Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    )
                  }
                ]}
                elevation={1}
              />
            </Grid>

            <Grid item xs={12} md={5}>
              <Grid container spacing={3} direction="column">
                <Grid item>
                  <DataPanel
                    title={messageSlices.length > 1 ? "Key Dates (All Messages)" : "Key Dates"}
                    subtitle="Dates referenced in the document"
                    tag={<DateRangeIcon fontSize="small" />}
                    elevation={1}
                  >
                    {(() => {
                      // Aggregate unique dates from all messages
                      const allDates = {};
                      
                      if (messageSlices.length > 1) {
                        messageSlices.forEach(({ parsed, meta }) => {
                          if (parsed.dates) {
                            Object.entries(parsed.dates).forEach(([qualifier, dateInfo]) => {
                              const key = `${qualifier}-${dateInfo.value}`;
                              if (!allDates[key]) {
                                allDates[key] = {
                                  ...dateInfo,
                                  qualifier,
                                  messages: [`Msg #${meta.index}`]
                                };
                              } else {
                                allDates[key].messages.push(`Msg #${meta.index}`);
                              }
                            });
                          }
                        });
                      } else {
                        if (parsedData.dates) {
                          Object.entries(parsedData.dates).forEach(([qualifier, dateInfo]) => {
                            allDates[qualifier] = { ...dateInfo, qualifier };
                          });
                        }
                      }
                      
                      return Object.keys(allDates).length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date Type</TableCell>
                              <TableCell>Value</TableCell>
                                {messageSlices.length > 1 && <TableCell>Messages</TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                              {Object.values(allDates).map((dateInfo, index) => (
                                <TableRow key={index}>
                                <TableCell>
                                  <CodeHighlight
                                      code={dateInfo.qualifier}
                                      description={dateInfo.qualifierMeaning || getDateQualifierDescription(dateInfo.qualifier)}
                                    type="qualifier"
                                  />
                                </TableCell>
                                <TableCell>
                                    {dateInfo.formatted || dateInfo.value}
                                </TableCell>
                                  {messageSlices.length > 1 && (
                                    <TableCell>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {(dateInfo.messages || []).map((msg, msgIndex) => (
                                          <Chip 
                                            key={msgIndex}
                                            label={msg} 
                                            size="small" 
                                            variant="outlined"
                                            color="secondary"
                                          />
                                        ))}
                                      </Box>
                                    </TableCell>
                                  )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert 
                        severity="info" 
                        sx={{ mt: 1 }}
                      >
                        No dates specified in this document
                      </Alert>
                      );
                    })()}
                  </DataPanel>
                </Grid>
                <Grid item>
                  <DataPanel
                    title="Document Statistics"
                    subtitle="Segment types found in this document"
                    tag={<AssessmentIcon fontSize="small" />}
                    elevation={1}
                  >
                    <Grid container spacing={1}>
                      {parsedData.segmentCounts && Object.keys(parsedData.segmentCounts).length > 0 ? (
                        Object.keys(parsedData.segmentCounts).sort().map((segmentType) => (
                          <Grid item xs={4} sm={3} key={segmentType}>
                            <Box 
                              sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1,
                                borderRadius: 1,
                                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                border: '1px solid',
                                borderColor: 'divider'
                              }}
                            >
                              <Chip
                                label={segmentType}
                                size="small"
                                color="primary"
                                sx={{ minWidth: '45px', fontWeight: 'medium' }}
                              />
                              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                                {parsedData.segmentCounts[segmentType]}
                              </Typography>
                            </Box>
                          </Grid>
                        ))
                      ) : (
                        <Grid item xs={12}>
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            No segment statistics available
                          </Alert>
                        </Grid>
                      )}
                    </Grid>
                  </DataPanel>
                </Grid>
              </Grid>
            </Grid>

            {/* Third row: MOA details and Line Items */}
            {(() => {
              // Check if we have any MOA segments across all messages
              const hasMOASegments = messageSlices.length > 1 
                ? messageSlices.some(({ parsed }) => parsed.segments?.some(seg => seg.id === 'MOA'))
                : parsedData.segments?.some(seg => seg.id === 'MOA');
              
              const hasDocumentTotals = parsedData.documentTotals?.documentTotal != null || 
              parsedData.documentTotals?.lineItemTotal != null || 
                                       parsedData.documentTotals?.taxTotal != null;
              
              return (hasMOASegments || hasDocumentTotals) && (
              <Grid item xs={12} md={6}>
                <DataPanel
                    title={messageSlices.length > 1 ? "Monetary Amounts Detail (All Messages)" : "Monetary Amounts Detail"}
                  subtitle="MOA segment details"
                  tag={<AssessmentIcon fontSize="small" />}
                  elevation={1}
                >
                      <Alert severity="info" sx={{ mb: 2 }}>
                      MOA (Monetary Amount) segments found in the document{messageSlices.length > 1 ? 's' : ''}:
                      </Alert>
                      
                    {messageSlices.length > 1 ? (
                      // Multiple messages: group MOA segments by message
                      messageSlices.map(({ meta: m, parsed }, msgIndex) => {
                        const moaSegments = parsed.segments?.filter(seg => seg.id === 'MOA') || [];
                        
                        if (moaSegments.length === 0) return null;
                        
                        return (
                          <Box key={msgIndex} sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                              <Chip 
                                label={`Msg #${m.index}`} 
                                size="small" 
                                color="primary" 
                                sx={{ mr: 1 }} 
                              />
                              {parsed.messageType || m.type || 'Unknown'} • Ref {parsed.messageReference || m.ref || 'N/A'}
                            </Typography>
                            
                            {moaSegments.map((segment, index) => {
                              let qualifier = '';
                              let amount = '';
                              let currency = '';
                              let description = 'Unknown';
                              
                              if (segment.elements && segment.elements.length > 0 && Array.isArray(segment.elements[0])) {
                                qualifier = segment.elements[0][0] || '';
                                amount = segment.elements[0][1] || '';
                                currency = segment.elements[0][2] || '';
                                
                                // Get qualifier description from the utility function
                                const qualifierDesc = getQualifierCodeExplanation('monetaryAmountQualifiers', qualifier);
                                description = qualifierDesc || `Amount (qualifier ${qualifier})`;
                              }
                              
                              return (
                                <Box 
                                  key={`${msgIndex}-${index}`} 
                                  sx={{ 
                                    mb: 1, 
                                    p: 1.5, 
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    backgroundColor: theme => {
                                      const baseColors = {
                                        '79': theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)', // Total
                                        '203': theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.08)', // Line amount
                                        '86': theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.15)' : 'rgba(156, 39, 176, 0.08)', // Tax amount
                                        '124': theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.08)', // Net amount
                                        '9': theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.15)' : 'rgba(255, 193, 7, 0.08)' // Amount due
                                      };
                                      return baseColors[qualifier] || 'inherit';
                                    }
                                  }}
                                >
                                  <Grid container spacing={1}>
                                    <Grid item xs={2}>
                                      <Tooltip title="MOA Qualifier Code" arrow>
                                        <Chip 
                                          label={qualifier} 
                                          size="small" 
                                          color={qualifier === '79' ? 'success' : 
                                                 qualifier === '203' ? 'primary' : 
                                                 qualifier === '86' ? 'secondary' :
                                                 qualifier === '124' ? 'warning' :
                                                 qualifier === '9' ? 'info' : 'default'} 
                                          sx={{ fontWeight: 'medium' }} 
                                          icon={<MonetizationOnIcon fontSize="small" />}
                                        />
                                      </Tooltip>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="body2">
                                        {description}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={4} sx={{ textAlign: 'right' }}>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {currency || parsed.documentTotals?.currency || ''} {parseFloat(amount).toFixed(2)}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                  
                                  {/* Documentation about this MOA code */}
                                  <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                      <strong>Definition:</strong> {description}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                      <strong>Segment:</strong> <code>MOA+{segment.elements[0].join(':')}'</code>
                                    </Typography>
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      }).filter(Boolean)
                    ) : (
                      // Single message: original logic
                      parsedData.segments
                        ?.filter(seg => seg.id === 'MOA')
                        .map((segment, index) => {
                          let qualifier = '';
                          let amount = '';
                          let currency = '';
                          let description = 'Unknown';
                          
                          if (segment.elements && segment.elements.length > 0 && Array.isArray(segment.elements[0])) {
                            qualifier = segment.elements[0][0] || '';
                            amount = segment.elements[0][1] || '';
                            currency = segment.elements[0][2] || '';
                            
                            // Get qualifier description from the utility function
                            const qualifierDesc = getQualifierCodeExplanation('monetaryAmountQualifiers', qualifier);
                            description = qualifierDesc || `Amount (qualifier ${qualifier})`;
                          }
                          
                          return (
                            <Box 
                              key={index} 
                              sx={{ 
                                mb: 1, 
                                p: 1.5, 
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                backgroundColor: theme => {
                                  const baseColors = {
                                    '79': theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)',
                                    '203': theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.08)',
                                    '86': theme.palette.mode === 'dark' ? 'rgba(156, 39, 176, 0.15)' : 'rgba(156, 39, 176, 0.08)',
                                    '124': theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.08)',
                                    '9': theme.palette.mode === 'dark' ? 'rgba(255, 193, 7, 0.15)' : 'rgba(255, 193, 7, 0.08)'
                                  };
                                  return baseColors[qualifier] || 'inherit';
                                }
                              }}
                            >
                              <Grid container spacing={1}>
                                <Grid item xs={2}>
                                  <Tooltip title="MOA Qualifier Code" arrow>
                                    <Chip 
                                      label={qualifier} 
                                      size="small" 
                                      color={qualifier === '79' ? 'success' : 
                                             qualifier === '203' ? 'primary' : 
                                             qualifier === '86' ? 'secondary' :
                                             qualifier === '124' ? 'warning' :
                                             qualifier === '9' ? 'info' : 'default'} 
                                      sx={{ fontWeight: 'medium' }} 
                                      icon={<MonetizationOnIcon fontSize="small" />}
                                    />
                                  </Tooltip>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="body2">
                                    {description}
                                  </Typography>
                                </Grid>
                                <Grid item xs={4} sx={{ textAlign: 'right' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {currency || parsedData.documentTotals?.currency || ''} {parseFloat(amount).toFixed(2)}
                                  </Typography>
                                </Grid>
                              </Grid>
                              
                              {/* Documentation about this MOA code */}
                              <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed', borderColor: 'divider' }}>
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                  <strong>Definition:</strong> {description}
                                </Typography>
                                <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                  <strong>Segment:</strong> <code>MOA+{segment.elements[0].join(':')}'</code>
                                </Typography>
                              </Box>
                            </Box>
                          );
                        })
                    )}
                        
                    {!hasMOASegments && (
                        <Alert severity="warning">
                        No MOA segments found in this document{messageSlices.length > 1 ? 's' : ''}.
                        </Alert>
                )}
              </DataPanel>
            </Grid>
              );
            })()}

            {/* Line Items: per-message when multiple */}
            {messageSlices.length > 1 ? (
              messageSlices.map(({ meta: m, parsed }, idx) => (
                <Grid item xs={12} key={`lines-${idx}`}>
                  <DataPanel
                    title={`Line Items (Msg #${idx+1} • ${parsed.messageType || m.type || 'Unknown'}${parsed.messageReference ? ` • Ref ${parsed.messageReference}` : ''})`}
                    subtitle="Products with identification details"
                    tag={<AssessmentIcon fontSize="small" />}
                    elevation={1}
                  >
                    {parsed.lineItems && parsed.lineItems.length > 0 ? (
                      <TableContainer sx={{ mb: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell width="5%">Line</TableCell>
                              <TableCell width="25%">Product ID</TableCell>
                              <TableCell width="25%">Description</TableCell>
                              <TableCell width="10%">Quantity</TableCell>
                              <TableCell width="15%">Price</TableCell>
                              <TableCell width="8%">VAT %</TableCell>
                              <TableCell width="10%">Dates</TableCell>
                              <TableCell width="10%" align="right">Total</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {parsed.lineItems.map((item, li) => (
                              <TableRow key={li} sx={{ '&:last-child td, &:last-child th': { border: 0 }, backgroundColor: li % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'inherit' }}>
                                <TableCell>{item.lineNumber || (li + 1)}</TableCell>
                                <TableCell>
                                  {item.productId ? (
                                    <Typography variant="body2" component="div">
                                      <CodeHighlight code={item.productId} description={item.idType ? `${item.idType} ID` : "Primary ID"} type="element" />
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">No primary ID</Typography>
                                  )}
                                  {item.additionalIds && item.additionalIds.length > 0 && (
                                    <Box sx={{ mt: 1 }}>
                                      {item.additionalIds.map((id, idIndex) => (
                                        <Typography key={idIndex} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                          <CodeHighlight code={id.productId} description={`${id.qualifierMeaning} (${id.qualifier})`} tooltip="PIA product identification" type="qualifier" />
                                          {id.idScheme && (
                                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled' }}>
                                              ({id.idScheme})
                                            </Typography>
                                          )}
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {item.description ? item.description : (
                                    <Typography variant="body2" color="text.secondary">No description</Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {item.quantity ? (
                                    <Tooltip title={`${item.quantity.qualifierMeaning} (${item.quantity.qualifier})${item.quantity.unit ? `, Unit: ${item.quantity.unit}` : ''}`} arrow>
                                      <Typography variant="body2">{item.quantity.value.toLocaleString()}</Typography>
                                    </Tooltip>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">N/A</Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {item.price ? (
                                    <Tooltip title={`${item.price.qualifierMeaning} (${item.price.qualifier})`} arrow>
                                      <Typography variant="body2">{parsed.documentTotals?.currency || ''} {item.price.value.toFixed(2)}</Typography>
                                    </Tooltip>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">N/A</Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {item.tax && item.tax.rate != null ? (
                                    <Typography variant="body2">{item.tax.rate}</Typography>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">-</Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {item.dates && Object.keys(item.dates).length > 0 ? (
                                    <Box>
                                      {Object.entries(item.dates).map(([qualifier, date], di) => (
                                        <Tooltip key={di} title={`${date.qualifierMeaning || `Date type ${qualifier}`} (${qualifier})`} arrow>
                                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                            {date.formatted || date.value}
                                          </Typography>
                                        </Tooltip>
                                      ))}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">N/A</Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  {item.itemTotal ? (
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {parsed.documentTotals?.currency || ''} {item.itemTotal.value.toFixed(2)}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">N/A</Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      parsed.segments && parsed.segments.some(seg => seg.id === 'LIN') && (
                        <Alert severity="info">Line items were detected but could not be fully parsed with product identification details.</Alert>
                      )
                    )}
                  </DataPanel>
                </Grid>
              ))
            ) : parsedData.lineItems && parsedData.lineItems.length > 0 ? (
              <Grid item xs={12}>
                <DataPanel
                  title="Line Items"
                  subtitle="Products with identification details"
                  tag={<AssessmentIcon fontSize="small" />}
                  elevation={1}
                >
                  <TableContainer sx={{ mb: 2 }}>
                    <Table size="small">
                          <TableHead>
                        <TableRow>
                          <TableCell width="5%">Line</TableCell>
                          <TableCell width="25%">Product ID</TableCell>
                          <TableCell width="25%">Description</TableCell>
                          <TableCell width="10%">Quantity</TableCell>
                          <TableCell width="15%">Price</TableCell>
                              <TableCell width="8%">VAT %</TableCell>
                          <TableCell width="10%">Dates</TableCell>
                          <TableCell width="10%" align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {parsedData.lineItems.map((item, index) => (
                          <TableRow key={index} sx={{ 
                            '&:last-child td, &:last-child th': { border: 0 },
                            backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'inherit'
                          }}>
                            <TableCell>{item.lineNumber || (index + 1)}</TableCell>
                            <TableCell>
                              {item.productId ? (
                                <Typography variant="body2" component="div">
                                  <CodeHighlight 
                                    code={item.productId} 
                                    description={item.idType ? `${item.idType} ID` : "Primary ID"} 
                                    type="element"
                                  />
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">No primary ID</Typography>
                              )}
                              
                              {/* Display PIA additional identifications */}
                              {item.additionalIds && item.additionalIds.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  {item.additionalIds.map((id, idIndex) => (
                                    <Typography key={idIndex} variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                      <CodeHighlight 
                                        code={id.productId} 
                                        description={`${id.qualifierMeaning} (${id.qualifier})`}
                                        tooltip="PIA product identification"
                                        type="qualifier"
                                      />
                                      {id.idScheme && (
                                        <Typography 
                                          component="span" 
                                          variant="caption" 
                                          sx={{ ml: 1, color: 'text.disabled' }}
                                        >
                                          ({id.idScheme})
                                        </Typography>
                                      )}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              {item.description ? (
                                item.description
                              ) : (
                                <Typography variant="body2" color="text.secondary">No description</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {item.quantity ? (
                                <Tooltip 
                                  title={`${item.quantity.qualifierMeaning} (${item.quantity.qualifier})${item.quantity.unit ? `, Unit: ${item.quantity.unit}` : ''}`} 
                                  arrow
                                >
                                  <Typography variant="body2">
                                    {item.quantity.value.toLocaleString()}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.secondary">N/A</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {item.price ? (
                                <Tooltip title={`${item.price.qualifierMeaning} (${item.price.qualifier})`} arrow>
                                  <Typography variant="body2">
                                    {parsedData.documentTotals?.currency || ''} {item.price.value.toFixed(2)}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.secondary">N/A</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {item.tax && item.tax.rate != null ? (
                                <Typography variant="body2">{item.tax.rate}</Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {item.dates && Object.keys(item.dates).length > 0 ? (
                                <Box>
                                  {Object.entries(item.dates).map(([qualifier, date], dateIndex) => (
                                    <Tooltip 
                                      key={dateIndex}
                                      title={`${date.qualifierMeaning || `Date type ${qualifier}`} (${qualifier})`} 
                                      arrow
                                    >
                                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                        {date.formatted || date.value}
                                      </Typography>
                                    </Tooltip>
                                  ))}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">N/A</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {item.itemTotal ? (
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {parsedData.documentTotals?.currency || ''} {item.itemTotal.value.toFixed(2)}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">N/A</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Explanation of PIA qualifiers */}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      PIA Segment Qualifiers
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '40px 1fr', gap: '4px 8px' }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>1</Typography>
                          <Typography variant="caption">Additional identification</Typography>
                          
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>2</Typography>
                          <Typography variant="caption">Product identification</Typography>
                          
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>3</Typography>
                          <Typography variant="caption">Supplier's identification</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '40px 1fr', gap: '4px 8px' }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>4</Typography>
                          <Typography variant="caption">Shipment identification</Typography>
                          
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>5</Typography>
                          <Typography variant="caption">Product group</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </DataPanel>
              </Grid>
            ) : (
              parsedData.segments && parsedData.segments.some(seg => seg.id === 'LIN') && (
                <Grid item xs={12}>
                  <DataPanel
                    title="Line Items"
                    elevation={1}
                  >
                    <Alert severity="info">
                      Line items were detected but could not be fully parsed with product identification details.
                    </Alert>
                  </DataPanel>
                </Grid>
              )
            )}
          </Grid>
        </Box>
      </TabPanel>

      {/* Raw EDI Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ px: 2 }}>
          <RawEdiViewer 
            content={ediContent} 
            validationResults={validationResults}
            parsedData={parsedData}
          />
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button size="small" variant="outlined" onClick={copyRawEdi}>Copy Raw</Button>
            <Button size="small" variant="outlined" onClick={downloadRawEdi}>Download .edi</Button>
          </Box>
        </Box>
      </TabPanel>

      {/* Reference Tab */}
      <TabPanel value={tabValue} index={3}>
        <Box sx={{ px: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 500, mb: 3 }}>
            EDIFACT Reference Guide
          </Typography>
          <Typography variant="body1" paragraph>
            This reference provides information about EDIFACT segments and qualifiers according to the UN/EDIFACT D96A standard and EANCOM implementation guidelines.
          </Typography>
          <EdifactReference />
        </Box>
      </TabPanel>

      {/* Validation Tab */}
      <TabPanel value={tabValue} index={4}>
        <Box sx={{ px: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>
              EANCOM Validation
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button 
                variant="outlined" 
                color="info"
                onClick={downloadValidationJson}
                disabled={!validationResults}
              >
                Download JSON
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                startIcon={<VerifiedIcon />}
                onClick={runValidation}
                disabled={isValidating}
              >
                Run STAND Validation
              </Button>
            </Box>
          </Box>
          
          {/* Internal validation tabs */}
          <Tabs
            value={validationTabIndex}
            onChange={handleValidationTabChange}
            aria-label="validation options"
            sx={{ mb: 3 }}
          >
            <Tab label="STAND Validation" />
            <Tab label="External Services" />
          </Tabs>
          
          {validationTabIndex === 0 ? (
            <>
              {validationResults || isValidating ? (
                <ValidationResults 
                  validationResults={validationResults} 
                  isValidating={isValidating}
                  messageType={parsedData.messageType}
                  distributionType={detectedDistribution}
                  autoValidate={autoValidate}
                  onToggleAutoValidate={() => setAutoValidate(v => !v)}
                  onNavigateToLocation={handleNavigateToLocation}
                />
              ) : (
                <Box sx={{ textAlign: 'center', p: 4, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    Click the "Run STAND Validation" button to check your document against EANCOM standards.
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <ExternalValidation ediContent={ediContent} />
          )}
        </Box>
      </TabPanel>
    </Box>
  );
};

export default EdiViewer;